import { ChevronLeft, ChevronRight, ImageIcon, Images, X } from "lucide-react";
import { Dialog, DialogClose, DialogContent } from "../ui/dialog";
import type { GalleryGroup, GalleryPhotoStop, GalleryView } from './gallery.groups';
import { SCROLL_STRIP_FRAME_CLASS, SCROLL_STRIP_HEADER_CLASS, SCROLL_STRIP_ITEM_CLASS, SCROLL_STRIP_TRACK_CLASS, useScrollStrip } from "../../lib/use-scroll-strip";
import { galleryViewsOf, getProjectPhotos, photoGroupsOf, viewIndexForImage, viewIndexForStop } from './gallery.groups';
import { imagePositionStyle, responsiveImageProps } from "../../lib/image-position";
import { BOLD_CONTAINER_CLASS } from "../ui/bold-container";
import type { BaseComponentProps } from "../../types/puck";
import { ComparisonFrame } from "../before-after/comparison-frame";
import { DisplayHeading } from "../ui/display-heading";
import type { GalleryItemKind } from './gallery.kind';
import type { ImagePosition } from '../../lib/image-position';
import { MS_PER_MINUTE } from "@servicepro/utils";
import React from "react";
import { ScrollStripArrows } from "../ui/scroll-strip-arrows";
import type { SliderWiggle } from "../before-after/slider-wiggle.fields";
import { SmartLink } from "../smart-link";
import { cn } from "../../lib/utils";
import { galleryItemKind } from './gallery.kind';
import { plainText } from "../../lib/plain-text";

/** One photo of a project. */
interface GalleryPhoto {
  imageUrl: string;
  alt?: string;
  caption?: string;
}

/** What a click on a project tile does. */
type GalleryClickAction = "gallery" | "link";

/**
 * One project in the gallery. `imageUrl` is the cover shown in the grid; `photos` is the
 * rest of that project's shoot, which the lightbox cycles through. Absent or empty
 * `photos` means the project is a single image, which is how every gallery read before
 * projects existed.
 */
interface GalleryImage {
  /**
   * What this entry is, which decides its settings in the editor and how it
   * renders. Absent on entries saved before the field; those are read from what
   * they hold. See gallery.kind.ts.
   */
  kind?: GalleryItemKind;
  imageUrl: string;
  alt: string;
  caption?: string;
  tag?: string;
  /** Where the photo keeps its focus when the tile crops it. */
  position?: ImagePosition;
  href?: string;
  /**
   * Whether clicking the tile opens this project's photos or follows `href`. Left unset it
   * falls back to the old implicit rule — a tile with a link navigated, one without opened
   * the lightbox — so tiles saved before this field existed keep behaving the same way.
   */
  clickAction?: GalleryClickAction;
  photos?: GalleryPhoto[];
  beforeImageUrl?: string;
  afterImageUrl?: string;
  beforeLabel?: string;
  afterLabel?: string;
  /**
   * Photo-library project this tile follows. While set, the project's current
   * photos are pulled live at render (the reviews-widget pattern), so photos
   * added to the project later show up without re-editing. Unsyncing in the
   * editor materializes the pulled list into `imageUrl`/`photos` and clears
   * this — data is copied on the way out, never deleted.
   */
  sourceProjectId?: string;
}

interface GalleryProps extends BaseComponentProps {
  heading: string;
  subheading?: string;
  images: GalleryImage[];
  /** "strip" is one row a visitor swipes through (CSS scroll-snap), with arrows on bigger screens. */
  layout?: "grid" | "showcase" | "masonry" | "strip" | "before-after";
  /**
   * Surface treatment: card chrome with hover lift, frameless editorial tiles,
   * or bold (a dark band, the heading in capitals, frameless tiles with captions under them).
   */
  variant?: "cards" | "editorial" | "bold";
  columns?: "2" | "3" | "4";
  showCaptions?: "yes" | "no";
  showTags?: "yes" | "no";
  sliderWiggle?: SliderWiggle;
  backgroundColor?: string;
  /** From Puck metadata in the editors; window config carries them on live sites. */
  puckSiteId?: string;
  puckApiUrl?: string;
}

/** Synced-project photos the gallery has pulled, by project id. */
type PulledPhotos = Partial<Record<string, GalleryPhoto[]>>;

/** Where the live project photos come from. "" when this render has no source. */
interface PhotoSource {
  apiUrl: string;
  siteId: string;
}

/** The section's photos as the lightbox pages them. */
interface GalleryContent {
  groups: GalleryGroup[];
  images: GalleryImage[];
  views: GalleryView[];
}

/** What the lightbox shows for the selected view. */
interface PhotoViewer {
  groupStopCount: number;
  nextGroup: GalleryGroup | null;
  selectedGroup: GalleryGroup | null;
  selectedImage: GalleryImage | null;
  selectedPhoto: GalleryPhoto | null;
  selectedPhotoCaption: string;
  selectedStop: GalleryPhotoStop | null;
  selectedView: GalleryView | null;
  showsLightboxCaption: boolean;
  showsLightboxTag: boolean;
}

const PROJECT_PHOTOS_CACHE_MINUTES = 5,
  PROJECT_PHOTOS_CACHE_TTL = PROJECT_PHOTOS_CACHE_MINUTES * MS_PER_MINUTE,

  /**
   * A comparison's side labels, "Before" and "After" unless the pro renamed them.
   * @param {GalleryImage} image - The comparison entry.
   * @returns {{ afterLabel: string; beforeLabel: string }} The label of each side.
   */
  beforeAfterLabels = (image: GalleryImage): { afterLabel: string; beforeLabel: string } => ({
    afterLabel: (image.afterLabel ?? "") || "After",
    beforeLabel: (image.beforeLabel ?? "") || "Before",
  }),

  /**
   * The accessible name of a comparison: "Compare Before and After", plus the
   * project's caption when it has one.
   * @param {GalleryImage} image - The comparison entry.
   * @returns {string} The label for the comparison frame.
   */
  comparisonGroupLabel = (image: GalleryImage): string => {
    const { afterLabel, beforeLabel } = beforeAfterLabels(image),
      caption = image.caption ?? "",
      compareText = `Compare ${beforeLabel} and ${afterLabel}`;
    if (caption === "") {
      return compareText;
    }
    return `${compareText}: ${caption}`;
  },

  /**
   * "1 photo", never "1 photos".
   * @param {number} count - How many photos.
   * @returns {string} The count with the noun that fits it.
   */
  countedPhotos = (count: number): string => {
    if (count === 1) {
      return `${count} photo`;
    }
    return `${count} photos`;
  },

  /**
   * The opening of the line that closes a group. A group of one gets its own
   * wording: "all 1 photo" is not a sentence.
   * @param {number} count - How many photos the group holds.
   * @returns {string} "That's the only photo" or "That's all N photos".
   */
  endOfGroupLead = (count: number): string => {
    if (count === 1) {
      return "That's the only photo";
    }
    return `That's all ${countedPhotos(count)}`;
  },

  /**
   * The line that closes a group. Named sets say which project ended, unnamed
   * ones say "this project", and the loose photos are simply photos.
   * @param {GalleryGroup} group - The group that just ended.
   * @returns {string} The closing line.
   */
  endOfGroupText = (group: GalleryGroup): string => {
    const lead = endOfGroupLead(group.stops.length);
    if (group.kind !== "set") {
      return lead;
    }
    if (group.label) {
      return `${lead} of ${group.label}`;
    }
    return `${lead} of this project`;
  },

  /**
   * Groups the shown images and lays out the views the lightbox pages through.
   * @param {GalleryImage[]} images - The images the section shows.
   * @returns {GalleryContent} The images with their groups and views.
   */
  galleryOf = (images: GalleryImage[]): GalleryContent => {
    const groups = photoGroupsOf(images);
    return { groups, images, views: galleryViewsOf(groups) };
  },

  /**
   * Grid column classes for the grid and showcase layouts.
   * @param {GalleryProps["columns"]} columns - The column count the pro chose.
   * @returns {string} The responsive grid-cols classes.
   */
  getGridCols = (columns: GalleryProps["columns"]): string => {
    switch (columns) {
      case "2": {
        return "jobsite:md:grid-cols-2";
      }
      case "4": {
        return "jobsite:sm:grid-cols-2 jobsite:xl:grid-cols-4";
      }
      default: {
        return "jobsite:sm:grid-cols-2 jobsite:lg:grid-cols-3";
      }
    }
  },
  /**
   * Column classes for the masonry layout.
   * @param {GalleryProps["columns"]} columns - The column count the pro chose.
   * @returns {string} The responsive columns classes.
   */
  getMasonryColumnsClass = (columns: GalleryProps["columns"]): string => {
    switch (columns) {
      case "2": {
        return "jobsite:sm:columns-2";
      }
      case "4": {
        return "jobsite:sm:columns-2 jobsite:xl:columns-4";
      }
      default: {
        return "jobsite:sm:columns-2 jobsite:lg:columns-3";
      }
    }
  },
  /**
   * How far the featured showcase tile spans.
   * @param {GalleryProps["columns"]} columns - The column count the pro chose.
   * @returns {string} The span classes, or "" where the tile spans nothing.
   */
  getShowcaseFeaturedSpan = (columns: GalleryProps["columns"]): string => {
    switch (columns) {
      case "3":
      case "4": {
        return "jobsite:lg:col-span-2 jobsite:lg:row-span-2";
      }
      default: {
        return "";
      }
    }
  },

  /**
   * Image sizing paired with getShowcaseMediaClass; see the WebKit caveat there.
   * @param {GalleryProps["columns"]} columns - The column count the pro chose.
   * @param {boolean} isFeatured - Whether this is the featured tile.
   * @param {boolean} isOverflow - Whether the tile sits in row 3 or later.
   * @returns {string} The image's sizing classes.
   */
  getShowcaseImageClass = (
    columns: GalleryProps["columns"],
    isFeatured: boolean,
    isOverflow: boolean,
  ): string => {
    if (columns === "3" || columns === "4") {
      if (isFeatured) {
        return "jobsite:h-full jobsite:object-cover jobsite:lg:h-auto";
      }
      if (isOverflow) {
        return "jobsite:h-full jobsite:object-cover";
      }
      return "jobsite:h-full jobsite:object-cover jobsite:lg:absolute jobsite:lg:inset-0 jobsite:lg:h-full jobsite:lg:w-full";
    }

    return "jobsite:h-full jobsite:object-cover";
  },

  /**
   * Media-box sizing for showcase tiles — the featured one and its siblings alike.
   *
   * At 3 and 4 columns the featured tile spans two grid rows from `lg` up, so the cell is
   * taller than any fixed ratio fills. Keeping an aspect ratio there is actively wrong: with a
   * definite stretched height, `aspect-ratio` computes the box's *width* from that height, so
   * the box grows wider than the card and everything positioned inside it (count badge,
   * caption) is pushed out past the clip. Drop the ratio where the tile spans and fill the cell
   * instead.
   *
   * The siblings need the same treatment for the mirror-image reason. The featured tile's own
   * height comes from its photo's proportions, so the two rows it spans end up taller than a
   * 4/3 box. A sibling card is a `<button>`, which centres its content, so the shortfall shows
   * up as equal bands above and below the photo rather than one gap underneath — which reads as
   * a broken image rather than a short one.
   *
   * WebKit caveat: the featured tile's height must come from an IN-FLOW image at its natural
   * proportions, and sibling images must be absolutely positioned fills. An in-flow `h-full`
   * image inside this auto-height chain is a percentage cycle that Chromium resolves to auto
   * but WebKit resolves to its maximum layout size (33,554,432px), destroying the page.
   *
   * The fill treatment only works for siblings sharing a row with the featured tile — those
   * rows get their height from it. A tile in row 3+ sits in an auto-height row, where an
   * absolutely positioned image contributes nothing and the whole card collapses to a sliver.
   * Overflow tiles keep the fixed ratio instead.
   * @param {GalleryProps["columns"]} columns - The column count the pro chose.
   * @param {boolean} isFeatured - Whether this is the featured tile.
   * @param {boolean} isOverflow - Whether the tile sits in row 3 or later.
   * @returns {string} The media box's sizing classes.
   */
  getShowcaseMediaClass = (
    columns: GalleryProps["columns"],
    isFeatured: boolean,
    isOverflow: boolean,
  ): string => {
    if (columns === "3" || columns === "4") {
      if (isFeatured) {
        return "jobsite:aspect-[4/3] jobsite:lg:aspect-auto";
      }
      if (isOverflow) {
        return "jobsite:aspect-[4/3]";
      }
      return "jobsite:aspect-[4/3] jobsite:lg:aspect-auto jobsite:lg:h-full";
    }

    return "jobsite:aspect-[4/3]";
  },

  /**
   * What to call a group on the card. A set a pro never named is still a
   * project; the loose group is just the rest of the photos.
   * @param {GalleryGroup} group - The group to name.
   * @returns {string} The group's title.
   */
  groupTitle = (group: GalleryGroup): string => {
    if (group.label) {
      return group.label;
    }
    if (group.kind === "set") {
      return "Another project";
    }
    return "More photos";
  },

  /**
   * A comparison needs both sides AND to be one: an entry a pro has set to Photo
   * renders as a photo even if it still carries a stale pair, so hiding those
   * settings in the editor can never leave the tile showing something else.
   * Entries saved before the kind existed are read from what they hold, which is
   * the rule this function used to apply on its own.
   * @param {GalleryImage} image - The gallery entry.
   * @returns {boolean} Whether the entry renders as a before/after comparison.
   */
  hasComparison = (image: GalleryImage): boolean => galleryItemKind(image) === "before-after" && Boolean(image.beforeImageUrl) && Boolean(image.afterImageUrl),

  /**
   * Whether an entry has any photo to show.
   * @param {GalleryImage} image - The gallery entry.
   * @returns {boolean} True when the cover, the before or the after photo is set.
   */
  hasPhoto = (image: GalleryImage): boolean => (image?.imageUrl ?? "") !== "" || (image?.beforeImageUrl ?? "") !== "" || (image?.afterImageUrl ?? "") !== "",

  /**
   * A synced entry with its project's pulled photos in place of the stored ones.
   * @param {GalleryImage} image - The synced gallery entry.
   * @param {GalleryPhoto[]} pulled - The project's current photos, at least one.
   * @returns {GalleryImage} The entry with its cover and photos from the project.
   */
  imageWithPhotos = (image: GalleryImage, pulled: GalleryPhoto[]): GalleryImage => {
    // The pulled list is the project's truth while synced: first photo
    // Covers the tile unless the pro chose a cover, the rest page through
    // The lightbox. Stored photos are ignored, not touched.
    const cover = image.imageUrl || pulled[0].imageUrl;
    return {
      ...image,
      imageUrl: cover,
      photos: pulled.filter((photo) => photo.imageUrl !== cover),
    };
  },

  /**
   * The images prop as a list; the editors can hand back something else.
   * @param {GalleryImage[]} images - The images prop.
   * @returns {GalleryImage[]} The images, or none.
   */
  imagesOf = (images: GalleryImage[]): GalleryImage[] => {
    if (Array.isArray(images)) {
      return images;
    }
    return [];
  },

  /**
   * The Next button's accessible name: on an end card it names the group it opens.
   * @param {GalleryView} view - The view on screen.
   * @param {GalleryGroup | null} nextGroup - The group the end card hands over to.
   * @returns {string} The button's label.
   */
  nextButtonLabel = (view: GalleryView, nextGroup: GalleryGroup | null): string => {
    if (view.kind === "end" && nextGroup !== null) {
      return `View ${nextGroup.label}`;
    }
    return "Next photo";
  },

  /**
   * The small heading over the next group on an end card.
   * @param {GalleryGroup} group - The next group.
   * @returns {string} "Next project" for a named set, otherwise "Next".
   */
  nextGroupHeading = (group: GalleryGroup): string => {
    if (group.kind === "set" && group.label !== "") {
      return "Next project";
    }
    return "Next";
  },

  /**
   * The group an end card hands the visitor to.
   * @param {GalleryGroup[]} groups - Every group in the section.
   * @param {GalleryView | null} view - The view on screen.
   * @returns {GalleryGroup | null} The next group, or null off an end card.
   */
  nextGroupOf = (groups: GalleryGroup[], view: GalleryView | null): GalleryGroup | null => {
    if (view?.kind !== "end") {
      return null;
    }
    return groups[view.nextGroupIndex] ?? null;
  },

  /**
   * Explicit choice wins; otherwise fall back to the pre-`clickAction` implicit rule.
   * @param {GalleryImage} image - The gallery entry.
   * @returns {boolean} Whether a click on the tile follows its link.
   */
  opensLink = (image: GalleryImage): boolean => {
    if ((image.href ?? "") === "") {
      return false;
    }

    return (image.clickAction ?? "link") === "link";
  },

  /**
   * Where to pull synced project photos from. Metadata props win in the editors
   * (window config is set after paint there); window config carries the ids on
   * live sites.
   * @param {string | undefined} puckSiteId - The site id from Puck metadata.
   * @param {string | undefined} puckApiUrl - The API URL from Puck metadata.
   * @returns {PhotoSource} The API URL and site id, "" where neither has one.
   */
  photoSourceOf = (puckSiteId: string | undefined, puckApiUrl: string | undefined): PhotoSource => {
    if (typeof window === "undefined") {
      return { apiUrl: puckApiUrl ?? "", siteId: puckSiteId ?? "" };
    }
    const windowConfig = (window as { __SERVICEPRO_CONFIG__?: { siteId?: string; apiUrl?: string } })
      .__SERVICEPRO_CONFIG__;
    return {
      apiUrl: (puckApiUrl ?? "") || (windowConfig?.apiUrl ?? ""),
      siteId: (puckSiteId ?? "") || (windowConfig?.siteId ?? ""),
    };
  },

  /** Live project photos, cached per project for the page view. */
  projectPhotosCache = new Map<string, { photos: GalleryPhoto[]; timestamp: number }>(),

  /**
   * The photos pulled for an entry's synced project.
   * @param {GalleryImage} image - The gallery entry.
   * @param {PulledPhotos} pulledPhotos - Every project's pulled photos.
   * @returns {GalleryPhoto[]} The project's photos, or none when the entry isn't synced or nothing has arrived.
   */
  pulledPhotosOf = (image: GalleryImage, pulledPhotos: PulledPhotos): GalleryPhoto[] => {
    const projectId = image?.sourceProjectId ?? "";
    if (projectId === "") {
      return [];
    }
    return pulledPhotos[projectId] ?? [];
  },

  /**
   * The first showcase tile that lands in row 3 or later.
   * @param {"3" | "4"} columns - The column count the pro chose.
   * @returns {number} The tile index where the overflow rows start.
   */
  showcaseOverflowStart = (columns: "3" | "4"): number => 1 + (Number(columns) - 2) * 2,

  /**
   * The images the section shows: synced projects carry their pulled photos,
   * and entries with no photo at all are left out.
   * @param {GalleryImage[]} images - The images prop.
   * @param {PulledPhotos} pulledPhotos - Every project's pulled photos.
   * @returns {GalleryImage[]} The images to render.
   */
  shownImages = (images: GalleryImage[], pulledPhotos: PulledPhotos): GalleryImage[] => imagesOf(images)
    .map((image) => {
      const pulled = pulledPhotosOf(image, pulledPhotos);
      if (pulled.length === 0) {
        return image;
      }
      return imageWithPhotos(image, pulled);
    })
    .filter((image) => hasPhoto(image)),

  /**
   * The open view's photo stop.
   * @param {GalleryGroup | null} group - The open group.
   * @param {GalleryView | null} view - The view on screen.
   * @returns {GalleryPhotoStop | null} The stop, or null on an end card or with nothing open.
   */
  stopOf = (group: GalleryGroup | null, view: GalleryView | null): GalleryPhotoStop | null => {
    if (view?.kind !== "photo") {
      return null;
    }
    return group?.stops[view.stopIndex] ?? null;
  },

  /**
   * The synced project ids as one string. The effect keys on the joined string,
   * not the array: the id list is rebuilt every render, and an array dep would
   * re-run the effect each time.
   * @param {GalleryImage[]} images - The images prop.
   * @returns {string} The sorted, distinct project ids joined by commas.
   */
  syncedProjectsKeyOf = (images: GalleryImage[]): string => [
    ...new Set(
      imagesOf(images).map((image) => image?.sourceProjectId).filter((id): id is string => Boolean(id)),
    ),
  ].sort().join(","),

  /**
   * A tile's React key.
   * @param {GalleryImage} image - The gallery entry.
   * @param {number} index - The tile's position in the section.
   * @returns {string} The entry's first photo URL or alt text, then its index.
   */
  tileKeyOf = (image: GalleryImage, index: number): string => `${(image.afterImageUrl ?? "") || (image.beforeImageUrl ?? "") || image.imageUrl || image.alt || String(index)}-${index}`,

  /**
   * The view at an index.
   * @param {GalleryView[]} views - Every view in the section.
   * @param {number | null} selected - The selected index, null when the lightbox is closed.
   * @returns {GalleryView | null} The view, or null with nothing selected.
   */
  viewAt = (views: GalleryView[], selected: number | null): GalleryView | null => {
    if (selected === null) {
      return null;
    }
    return views[selected] ?? null;
  },

  /**
   * Everything the lightbox shows for the selected view.
   * @param {number | null} selected - Index into the section's views, null when closed.
   * @param {GalleryContent} gallery - The section's images, groups and views.
   * @param {Pick<GalleryProps, "showCaptions" | "showTags">} toggles - The section's caption and tag toggles.
   * @returns {PhotoViewer} The open view, its group, stop, image and photo, and what to show under it.
   */
  viewerOf = (
    selected: number | null,
    gallery: GalleryContent,
    toggles: Pick<GalleryProps, "showCaptions" | "showTags">,
  ): PhotoViewer => {
    const current = viewAt(gallery.views, selected),
      group = current && (gallery.groups[current.groupIndex] ?? null),
      stop = stopOf(group, current),
      stopImage = stop && (gallery.images[stop.imageIndex] ?? null),
      stopPhoto = stop?.photo ?? null,
      // Per-photo caption when the pro wrote one, otherwise the project's own caption.
      stopPhotoCaption = (stopPhoto?.caption ?? "") || (stopImage?.caption ?? "");

    return {
      // The counter and the thumbnail strip describe the OPEN group, not the
      // Section: "3 / 8" of a project a visitor can see the whole of.
      groupStopCount: group?.stops.length ?? 0,
      nextGroup: nextGroupOf(gallery.groups, current),
      selectedGroup: group,
      selectedImage: stopImage,
      selectedPhoto: stopPhoto,
      selectedPhotoCaption: stopPhotoCaption,
      selectedStop: stop,
      selectedView: current,
      // The toggles govern the lightbox too. They used to apply only to the tiles, so turning
      // Captions off still showed them the moment a visitor opened a photo.
      showsLightboxCaption: toggles.showCaptions === "yes" && stopPhotoCaption !== "",
      showsLightboxTag: toggles.showTags === "yes" && Boolean(stopImage?.tag),
    };
  };

const
  /**
   * Legacy Gallery mode (layout: "before-after"), kept rendering forever for
   * saved rows. The card is chrome around the shared ComparisonFrame, so old
   * galleries get the same on-image interaction as the BeforeAfter component.
   * @param {object} props - Component props.
   * @param {string} props.heading - The section heading, the alt text of last resort.
   * @param {GalleryImage} props.image - The comparison entry.
   * @param {GalleryProps["showCaptions"]} props.showCaptions - Whether captions show.
   * @param {GalleryProps["showTags"]} props.showTags - Whether tags show.
   * @param {React.CSSProperties} props.surfaceRadiusStyle - The site's corner radius.
   * @returns {React.ReactElement} The comparison card.
   */
  BeforeAfterGalleryCard = ({
    heading,
    image,
    showCaptions,
    showTags,
    surfaceRadiusStyle,
    wiggle,
  }: {
    heading: string;
    image: GalleryImage;
    showCaptions: GalleryProps["showCaptions"];
    showTags: GalleryProps["showTags"];
    surfaceRadiusStyle: React.CSSProperties;
    wiggle: boolean;
  }) => {
    const { afterLabel, beforeLabel } = beforeAfterLabels(image),
      afterImageUrl = (image.afterImageUrl ?? "") || image.imageUrl,
      beforeImageUrl = (image.beforeImageUrl ?? "") || image.imageUrl,
      comparisonAlt = image.alt || (image.caption ?? "") || plainText(heading),
      hasBothSides = Boolean(beforeImageUrl) && Boolean(afterImageUrl),
      showsCaption = showCaptions === "yes" && (image.caption ?? "") !== "",
      showsTag = showTags === "yes" && (image.tag ?? "") !== "";

    return (
      <div
        className="jobsite:overflow-hidden jobsite:border jobsite:border-border/70 jobsite:bg-background jobsite:shadow-sm"
        style={surfaceRadiusStyle}
      >
        {hasBothSides && (
          <ComparisonFrame
            beforeSrc={beforeImageUrl}
            afterSrc={afterImageUrl}
            beforeAlt={`${beforeLabel}: ${comparisonAlt}`}
            afterAlt={`${afterLabel}: ${comparisonAlt}`}
            beforeLabel={beforeLabel}
            afterLabel={afterLabel}
            groupLabel={comparisonGroupLabel(image)}
            wiggle={wiggle}
          />
        )}
        {!hasBothSides && (
          <div className="jobsite:relative jobsite:aspect-[4/3] jobsite:overflow-hidden">
            <img
              {...responsiveImageProps(beforeImageUrl || afterImageUrl, "grid_tile")}
              alt={comparisonAlt}
              className="jobsite:h-full jobsite:w-full jobsite:object-cover"
              loading="lazy"
            />
          </div>
        )}

        {(showsTag || showsCaption) && (
          <div className="jobsite:space-y-2 jobsite:p-4 jobsite:sm:p-5">
            {showsTag && (
              <span
                className="jobsite:inline-flex jobsite:bg-accent/15 jobsite:px-3 jobsite:py-1 jobsite:text-[11px] jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.18em] jobsite:text-accent"
                style={surfaceRadiusStyle}
              >
                {image.tag}
              </span>
            )}
            {showsCaption && (
              <div className="jobsite:font-heading jobsite:text-xl jobsite:text-foreground">
                {image.caption}
              </div>
            )}
          </div>
        )}
      </div>
    );
  },

  /**
   * The stop that closes a group and hands the visitor the next one. Paging used
   * to run straight from one project into the next, so the photos changed
   * subject with nothing to say they had; this says it, and makes going on a
   * choice.
   * @param {object} props - Component props.
   * @param {GalleryGroup} props.group - The group that just ended.
   * @param {GalleryGroup} props.nextGroup - The group the card offers next.
   * @param {() => void} props.onView - Opens the next group.
   * @returns {React.ReactElement} The end-of-group card.
   */
  EndOfGroupCard = ({
    group,
    nextGroup,
    onView,
  }: {
    group: GalleryGroup;
    nextGroup: GalleryGroup;
    onView: () => void;
  }) => {
    const cover = nextGroup.stops[0]?.photo.imageUrl;

    return (
      <div className="jobsite:flex jobsite:max-h-full jobsite:w-full jobsite:max-w-md jobsite:flex-col jobsite:items-center jobsite:gap-5 jobsite:rounded-lg jobsite:border jobsite:border-white/15 jobsite:bg-white/5 jobsite:px-6 jobsite:py-8 jobsite:text-center jobsite:backdrop-blur">
        <p className="jobsite:text-sm jobsite:text-white/70">{endOfGroupText(group)}</p>
        <button
          type="button"
          onClick={onView}
          className="jobsite:flex jobsite:w-full jobsite:items-center jobsite:gap-4 jobsite:rounded-lg jobsite:bg-white/10 jobsite:p-3 jobsite:text-left jobsite:transition-colors jobsite:hover:bg-white/20"
        >
          {Boolean(cover) && (
            <img
              {...responsiveImageProps(cover, "thumbnail")}
              alt=""
              loading="lazy"
              className="jobsite:h-16 jobsite:w-24 jobsite:shrink-0 jobsite:rounded-md jobsite:object-cover"
            />
          )}
          <span className="jobsite:min-w-0 jobsite:flex-1">
            <span className="jobsite:block jobsite:text-[11px] jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.18em] jobsite:text-white/60">
              {nextGroupHeading(nextGroup)}
            </span>
            <span className="jobsite:block jobsite:truncate jobsite:font-heading jobsite:text-lg jobsite:text-white">
              {groupTitle(nextGroup)}
            </span>
            <span className="jobsite:block jobsite:text-sm jobsite:text-white/70">
              {countedPhotos(nextGroup.stops.length)}
            </span>
          </span>
          <ChevronRight className="jobsite:h-5 jobsite:w-5 jobsite:shrink-0 jobsite:text-white/70" />
        </button>
      </div>
    );
  },

  Gallery: React.FC<GalleryProps> = ({
    heading,
    subheading,
    images: imagesProp,
    layout = "grid",
    variant = "cards",
    columns = "3",
    showCaptions = "yes",
    showTags = "yes",
    sliderWiggle = "on",
    backgroundColor,
    puckSiteId,
    puckApiUrl,
    id: sectionId,
    className,
  }) => {
    /**
     * Index into `views` — every group's photos in order, each closed by the card
     * that names the next group. Paging is scoped to the open group, so a
     * visitor walking one project never lands in another project's photos.
     */
    const [selected, setSelected] = React.useState<number | null>(null),
      strip = useScrollStrip(),
      stripTrackId = `${React.useId()}-photos`;
    const [pulledPhotos, setPulledPhotos] = React.useState<PulledPhotos>({}),
      // Computed fresh every render on purpose: the editors can hand back the
      // Same array reference with different contents, so memoizing on it made
      // Newly added projects invisible until an unrelated change re-rendered.
      gallery = galleryOf(shownImages(imagesProp, pulledPhotos)),
      { groups, images, views } = gallery,
      {
        groupStopCount,
        nextGroup,
        selectedGroup,
        selectedImage,
        selectedPhoto,
        selectedPhotoCaption,
        selectedStop,
        selectedView,
        showsLightboxCaption,
        showsLightboxTag,
      } = viewerOf(selected, gallery, { showCaptions, showTags }),
      { length: viewCount } = views,
      // Live photos for synced projects.
      { apiUrl, siteId } = photoSourceOf(puckSiteId, puckApiUrl),
      hasImages = images.length > 0,
      isBeforeAfterLayout = layout === "before-after",
      // Editorial drops the card chrome: no border, no shadow, no lift — wider gutters and
      // Quiet captions under the photo let the photography carry the section.
      isEditorial = variant === "editorial" && !isBeforeAfterLayout,
      // Bold is the same frameless tile on a dark band, so photos read brighter.
      isBold = variant === "bold" && !isBeforeAfterLayout,
      // Light type only on bold's own dark band: a colour the owner set is painted instead of it.
      onDarkBand = isBold && (backgroundColor ?? "") === "",
      isFrameless = isEditorial || isBold,
      isMasonryLayout = layout === "masonry",
      isShowcaseLayout = layout === "showcase",
      isStripLayout = layout === "strip",
      openImage = (index: number): void => {
        // The tile's own cover is where its group opens.
        const view = viewIndexForImage(views, groups, index);
        if (view === -1) {
          return;
        }
        setSelected(view);
      },
      surfaceRadiusStyle = { borderRadius: "var(--jobsite-radius)" } as const,
      syncedProjectsKey = syncedProjectsKeyOf(imagesProp),
      thumbnailStripRef = React.useRef<HTMLDivElement>(null);
    const moveSelection = React.useCallback(
      (direction: -1 | 1) => {
        setSelected((previous) => {
          if (previous === null || viewCount === 0) {
            return previous;
          }
          return (previous + direction + viewCount) % viewCount;
        });
      },
      [viewCount],
    );

    React.useEffect(() => {
      // An empty key splits into one empty id.
      const projectIds = syncedProjectsKey.split(",").filter((id) => id !== ""),
        pull = new AbortController();
      if (siteId === "" || apiUrl === "" || projectIds.length === 0) {
        return;
      }
      for (const projectId of projectIds) {
        const cached = projectPhotosCache.get(projectId);
        if (cached && Date.now() - cached.timestamp < PROJECT_PHOTOS_CACHE_TTL) {
          setPulledPhotos((previous) => {
            if (previous[projectId]) {
              return previous;
            }
            return { ...previous, [projectId]: cached.photos };
          },
          );
          continue;
        }
        fetch(`${apiUrl}/api/sites/public/${siteId}/projects/${projectId}/photos`)
          .then((response) => {
            if (response.ok) {
              return response.json();
            }
            return null;
          })
          .then((data: { photos?: { url: string; altText: string | null }[] } | null) => {
            if (pull.signal.aborted || !data?.photos) {
              return;
            }
            const photos = data.photos.map((photo) => ({
              alt: photo.altText ?? undefined,
              imageUrl: photo.url,
            }));
            projectPhotosCache.set(projectId, { photos, timestamp: Date.now() });
            setPulledPhotos((previous) => ({ ...previous, [projectId]: photos }));
          })
          .catch(() => {
            // A failed pull leaves the item rendering its stored data.
          });
      }
      return () => {
        pull.abort();
      };
    }, [siteId, apiUrl, syncedProjectsKey]);

    // Arrow keys page through the section. Escape is Radix's job.
    React.useEffect(() => {
      if (selected === null) {
        return;
      }

      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          moveSelection(-1);
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          moveSelection(1);
        }
      };

      window.addEventListener("keydown", onKeyDown);
      return () => {
        window.removeEventListener("keydown", onKeyDown);
      };
    }, [selected, moveSelection]);

    // Keep the active thumbnail in view when paging with the arrows or the keyboard.
    React.useEffect(() => {
      thumbnailStripRef.current
        ?.querySelector('[data-active="true"]')
        ?.scrollIntoView({ block: "nearest", inline: "center" });
    }, [selected]);

    const standardHeading = (
        <>
          <h2 className="jobsite:font-heading jobsite:text-3xl jobsite:tracking-tight jobsite:text-balance jobsite:text-foreground jobsite:sm:text-4xl jobsite:md:text-5xl">
            {heading}
          </h2>
          {(subheading ?? "") !== "" && (
            <p className="jobsite:mt-4 jobsite:text-lg jobsite:text-pretty jobsite:text-muted-foreground jobsite:md:text-xl">
              {subheading}
            </p>
          )}
        </>
      ),
      /**
       * The section's heading block. A strip puts its arrows beside the heading, so
       * its heading sits left; bold sets it in capitals on the dark band.
       * @returns {React.ReactNode} The heading block.
       */
      renderHeader = (): React.ReactNode => {
        if (isStripLayout) {
          return (
            <div className={SCROLL_STRIP_HEADER_CLASS}>
              {isBold ? (
                <DisplayHeading heading={heading} subheading={subheading} onDark={onDarkBand} className="jobsite:mb-0 jobsite:md:mb-0" />
              ) : (
                <div className="jobsite:max-w-3xl">{standardHeading}</div>
              )}
              {hasImages && <ScrollStripArrows strip={strip} trackId={stripTrackId} itemsLabel="photos" onDark={onDarkBand} />}
            </div>
          );
        }
        if (isBold) {
          return <DisplayHeading heading={heading} subheading={subheading} onDark={onDarkBand} />;
        }
        return <div className="jobsite:mx-auto jobsite:mb-12 jobsite:max-w-3xl jobsite:text-center">{standardHeading}</div>;
      };

    return (
      <>
        <section
          id={sectionId}
          className={cn(
            "jobsite:py-16 jobsite:md:py-24",
            isStripLayout && "jobsite:overflow-x-clip",
            (backgroundColor ?? "") === "" && !isBold && "jobsite:bg-background",
            // Bold is a dark band: photos read brighter on it, and it breaks up a long light page.
            (backgroundColor ?? "") === "" && isBold && "jobsite:bg-primary jobsite:text-primary-foreground",
            className,
          )}
          style={{ backgroundColor: (backgroundColor ?? "") || undefined }}
        >
          <div className={cn(isBold ? BOLD_CONTAINER_CLASS : "jobsite:container jobsite:mx-auto jobsite:px-4", isStripLayout && SCROLL_STRIP_FRAME_CLASS)}>
            {renderHeader()}

            {hasImages && (
              <div
                id={isStripLayout ? stripTrackId : undefined}
                ref={isStripLayout ? strip.trackRef : undefined}
                className={cn(
                  isStripLayout && SCROLL_STRIP_TRACK_CLASS,
                  !isStripLayout && isFrameless && "jobsite:gap-6 jobsite:md:gap-8",
                  !isStripLayout && !isFrameless && "jobsite:gap-4 jobsite:md:gap-5",
                  !isStripLayout && isMasonryLayout && getMasonryColumnsClass(columns),
                  !isStripLayout && !isMasonryLayout && cn("jobsite:grid", getGridCols(columns)),
                )}
              >
                {images.map((image, index) => {
                  if (isBeforeAfterLayout) {
                    return (
                      <BeforeAfterGalleryCard
                        key={tileKeyOf(image, index)}
                        heading={heading}
                        image={image}
                        showCaptions={showCaptions}
                        showTags={showTags}
                        surfaceRadiusStyle={surfaceRadiusStyle}
                        wiggle={sliderWiggle !== "off"}
                      />
                    );
                  }

                  const displayImageUrl =
                      image.imageUrl || (image.afterImageUrl ?? "") || image.beforeImageUrl,
                    hasCaptionText =
                      (showTags === "yes" && Boolean(image.tag)) || (showCaptions === "yes" && Boolean(image.caption)),
                    hasOverlayText = hasCaptionText && !isFrameless,
                    isFeatured = isShowcaseLayout && index === 0,
                    // The featured tile spans rows 1-2, leaving (columns - 2) * 2
                    // Sibling slots beside it; everything after lands in row 3+.
                    isShowcaseOverflow =
                      isShowcaseLayout &&
                      (columns === "3" || columns === "4") &&
                      index >= showcaseOverflowStart(columns),
                    photoCount = getProjectPhotos(image).length,
                    showsTileCaption = showCaptions === "yes" && (image.caption ?? "") !== "",
                    showsTileTag = showTags === "yes" && (image.tag ?? "") !== "",
                    tileClassName = cn(
                      "jobsite:group jobsite:relative jobsite:block jobsite:w-full jobsite:overflow-hidden jobsite:bg-background jobsite:text-left",
                      // No lift-and-drop-shadow on hover: the photo is the subject,
                      // And a card jumping toward the cursor reads as a widget. The
                      // Border tint plus the image's own slow zoom is the whole
                      // Hover gesture.
                      !isFrameless &&
                        "jobsite:border jobsite:border-border/70 jobsite:shadow-sm jobsite:transition-colors jobsite:duration-200 jobsite:hover:border-primary/25",
                      // On the dark band the caption under the photo sits on the band itself.
                      isBold && "jobsite:bg-transparent",
                      (isBold || isStripLayout) &&
                        "jobsite:focus-visible:outline-none jobsite:focus-visible:ring-[3px] jobsite:focus-visible:ring-ring",
                      isStripLayout && SCROLL_STRIP_ITEM_CLASS,
                      isFeatured && getShowcaseFeaturedSpan(columns),
                      // Fill the stretched grid cell so the media inside has a definite height to
                      // Resolve `h-full` against. Overflow tiles skip it: their row is auto-height,
                      // And a percentage against it is the cycle the WebKit caveat above warns about.
                      isShowcaseLayout && !isShowcaseOverflow && "jobsite:lg:h-full",
                      // Multi-column flow, not grid: keep tiles whole and space them vertically.
                      isMasonryLayout && isFrameless && "jobsite:mb-6 jobsite:break-inside-avoid jobsite:md:mb-8",
                      isMasonryLayout && !isFrameless && "jobsite:mb-4 jobsite:break-inside-avoid jobsite:md:mb-5",
                    ),
                    tileContent = (
                      <>
                        <div
                          className={cn(
                            "jobsite:relative jobsite:overflow-hidden",
                            // Masonry keeps the photo's own proportions; every other layout crops
                            // To a shared ratio so the grid stays even.
                            !isMasonryLayout && isShowcaseLayout && getShowcaseMediaClass(columns, isFeatured, isShowcaseOverflow),
                            !isMasonryLayout && !isShowcaseLayout && !isStripLayout && "jobsite:aspect-[4/3]",
                            // Taller on a phone, where the tile is most of the screen's width.
                            isStripLayout && "jobsite:aspect-[4/5] jobsite:md:aspect-[4/3]",
                          )}
                        >
                          <img
                            {...responsiveImageProps(displayImageUrl, "grid_tile")}
                            alt={image.alt || (image.caption ?? "") || plainText(heading)}
                            style={imagePositionStyle(image.position)}
                            className={cn(
                              "jobsite:w-full jobsite:transition-transform jobsite:duration-500 jobsite:group-hover:scale-[1.03]",
                              isMasonryLayout && "jobsite:h-auto",
                              !isMasonryLayout && isShowcaseLayout && getShowcaseImageClass(columns, isFeatured, isShowcaseOverflow),
                              !isMasonryLayout && !isShowcaseLayout && "jobsite:h-full jobsite:object-cover",
                            )}
                            loading="lazy"
                          />
                          {photoCount > 1 && (
                            <span
                              className="jobsite:absolute jobsite:right-3 jobsite:top-3 jobsite:flex jobsite:items-center jobsite:gap-1.5 jobsite:bg-background/80 jobsite:px-2.5 jobsite:py-1 jobsite:text-[11px] jobsite:font-semibold jobsite:text-foreground jobsite:backdrop-blur"
                              style={surfaceRadiusStyle}
                            >
                              <Images className="jobsite:h-3.5 jobsite:w-3.5" />
                              {photoCount}
                              <span className="jobsite:sr-only">photos in this project</span>
                            </span>
                          )}
                          {hasComparison(image) && (
                            <span
                              className="jobsite:absolute jobsite:left-3 jobsite:top-3 jobsite:bg-background/80 jobsite:px-2.5 jobsite:py-1 jobsite:text-[11px] jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.14em] jobsite:text-foreground jobsite:backdrop-blur"
                              style={surfaceRadiusStyle}
                            >
                              {(image.beforeLabel ?? "") || "Before"} / {(image.afterLabel ?? "") || "After"}
                            </span>
                          )}
                          {hasOverlayText && (
                            <div className="jobsite:absolute jobsite:inset-x-0 jobsite:bottom-0 jobsite:h-2/5 jobsite:bg-linear-to-t jobsite:from-black/80 jobsite:via-black/40 jobsite:to-transparent" />
                          )}
                          {hasOverlayText && (
                            <div className="jobsite:absolute jobsite:inset-x-0 jobsite:bottom-0 jobsite:flex jobsite:flex-col jobsite:gap-2 jobsite:p-4 jobsite:sm:p-5">
                              {showsTileTag && (
                                <span
                                  className="jobsite:w-fit jobsite:bg-background/80 jobsite:px-3 jobsite:py-1 jobsite:text-[11px] jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.18em] jobsite:text-accent jobsite:backdrop-blur"
                                  style={surfaceRadiusStyle}
                                >
                                  {image.tag}
                                </span>
                              )}
                              {showsTileCaption && (
                                <div className="jobsite:max-w-2xl jobsite:font-heading jobsite:text-lg jobsite:text-white jobsite:[text-shadow:0_1px_10px_rgba(0,0,0,0.45)] jobsite:sm:text-xl">
                                  {image.caption}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        {isFrameless && hasCaptionText && (
                          <div className="jobsite:flex jobsite:flex-col jobsite:gap-1 jobsite:pt-3">
                            {showsTileTag && (
                              <span
                                className={cn(
                                  "jobsite:text-[11px] jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.18em]",
                                  onDarkBand ? "jobsite:text-primary-foreground/70" : "jobsite:text-muted-foreground",
                                )}
                              >
                                {image.tag}
                              </span>
                            )}
                            {showsTileCaption && (
                              <div
                                className={cn(
                                  "jobsite:font-heading jobsite:text-base",
                                  onDarkBand ? "jobsite:text-primary-foreground" : "jobsite:text-foreground",
                                )}
                              >
                                {image.caption}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    );

                  if (opensLink(image)) {
                    return (
                      <SmartLink
                        key={tileKeyOf(image, index)}
                        to={image.href}
                        className={tileClassName}
                        style={surfaceRadiusStyle}
                      >
                        {tileContent}
                      </SmartLink>
                    );
                  }

                  return (
                    <button
                      key={tileKeyOf(image, index)}
                      type="button"
                      onClick={() => {
                        openImage(index);
                      }}
                      className={tileClassName}
                      style={surfaceRadiusStyle}
                    >
                      {tileContent}
                    </button>
                  );
                })}
              </div>
            )}
            {!hasImages && (
              <div
                className={cn(
                  "jobsite:flex jobsite:min-h-[320px] jobsite:flex-col jobsite:items-center jobsite:justify-center jobsite:rounded-[calc(var(--jobsite-radius)+8px)] jobsite:border jobsite:border-dashed jobsite:border-border jobsite:bg-muted/30 jobsite:px-6 jobsite:text-center",
                  // Keep the prompt readable on the dark band.
                  onDarkBand && "jobsite:border-primary-foreground/30 jobsite:bg-primary-foreground/5",
                )}
              >
                <div className="jobsite:mb-4 jobsite:flex jobsite:h-14 jobsite:w-14 jobsite:items-center jobsite:justify-center jobsite:rounded-full jobsite:bg-background jobsite:text-muted-foreground">
                  <ImageIcon className="jobsite:h-7 jobsite:w-7" />
                </div>
                <h3 className={cn("jobsite:font-heading jobsite:text-xl jobsite:text-foreground", onDarkBand && "jobsite:text-primary-foreground")}>
                  Add project photos
                </h3>
                <p
                  className={cn(
                    "jobsite:mt-2 jobsite:max-w-md jobsite:text-sm jobsite:text-muted-foreground",
                    onDarkBand && "jobsite:text-primary-foreground/75",
                  )}
                >
                  Upload before-and-after shots, recent installs, or finished work to turn this
                  section into visual proof.
                </p>
              </div>
            )}
          </div>
        </section>

        <Dialog
          open={!isBeforeAfterLayout && selected !== null}
          onOpenChange={(open) => {
            if (!open) {
              setSelected(null);
            }
          }}
        >
          <DialogContent
            showCloseButton={false}
            overlayClassName="jobsite:bg-black/85"
            className="jobsite:border-none jobsite:bg-transparent jobsite:p-0 jobsite:shadow-none jobsite:sm:max-w-[95vw]!"
          >
            {selectedView !== null && (
              // A photo viewer, not a card: chrome stays dark and out of the way at every site
              // Theme so the photo is the only lit thing on screen.
              <div className="jobsite:relative jobsite:mx-auto jobsite:flex jobsite:w-full jobsite:min-w-0 jobsite:max-w-6xl jobsite:flex-col jobsite:items-center jobsite:gap-4">
                {/*
                  The stage is a fixed box and the photo is centred inside it. Anchoring the
                  controls to the photo instead would move them every time a portrait shot
                  followed a landscape one, so paging through a project meant chasing the
                  arrows around the screen.
                */}
                <div className="jobsite:relative jobsite:flex jobsite:h-[68vh] jobsite:w-full jobsite:min-w-0 jobsite:items-center jobsite:justify-center">
                  {selectedView.kind === "end" && selectedGroup !== null && nextGroup !== null && (
                    <EndOfGroupCard
                      group={selectedGroup}
                      nextGroup={nextGroup}
                      onView={() => {
                        moveSelection(1);
                      }}
                    />
                  )}
                  {selectedImage !== null && selectedPhoto !== null && selectedStop?.photoIndex === 0 && hasComparison(selectedImage) && (
                    // The cover of a comparison project is the comparison
                    // Itself: the same frame, interactive, in place of a flat
                    // Photo. Paging goes on to the project's other photos.
                    <div className="jobsite:max-h-full jobsite:w-full jobsite:max-w-4xl jobsite:overflow-hidden jobsite:rounded-lg jobsite:shadow-2xl">
                      <ComparisonFrame
                        beforeSrc={selectedImage.beforeImageUrl as string}
                        afterSrc={selectedImage.afterImageUrl as string}
                        beforeAlt={`${(selectedImage.beforeLabel ?? "") || "Before"}: ${selectedImage.alt || selectedPhotoCaption || plainText(heading)}`}
                        afterAlt={`${(selectedImage.afterLabel ?? "") || "After"}: ${selectedImage.alt || selectedPhotoCaption || plainText(heading)}`}
                        beforeLabel={(selectedImage.beforeLabel ?? "") || "Before"}
                        afterLabel={(selectedImage.afterLabel ?? "") || "After"}
                        groupLabel={comparisonGroupLabel(selectedImage)}
                        eager
                        wiggle={sliderWiggle !== "off"}
                      />
                    </div>
                  )}
                  {selectedImage !== null && selectedPhoto !== null && !(selectedStop?.photoIndex === 0 && hasComparison(selectedImage)) && (
                    <img
                      {...responsiveImageProps(selectedPhoto.imageUrl, "lightbox")}
                      alt={
                        (selectedPhoto.alt ?? "") ||
                        selectedImage.alt ||
                        selectedPhotoCaption ||
                        plainText(heading)
                      }
                      className="jobsite:max-h-full jobsite:max-w-full jobsite:rounded-lg jobsite:object-contain jobsite:shadow-2xl"
                    />
                  )}
                  <DialogClose className="jobsite:absolute jobsite:right-0 jobsite:top-0 jobsite:flex jobsite:h-10 jobsite:w-10 jobsite:items-center jobsite:justify-center jobsite:rounded-full jobsite:bg-black/55 jobsite:text-white jobsite:backdrop-blur jobsite:transition-colors jobsite:hover:bg-black/75">
                    <X className="jobsite:h-4 jobsite:w-4" />
                    <span className="jobsite:sr-only">Close</span>
                  </DialogClose>
                  {viewCount > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          moveSelection(-1);
                        }}
                        aria-label="Previous photo"
                        className="jobsite:absolute jobsite:left-0 jobsite:top-1/2 jobsite:flex jobsite:h-11 jobsite:w-11 jobsite:-translate-y-1/2 jobsite:items-center jobsite:justify-center jobsite:rounded-full jobsite:bg-black/55 jobsite:text-white jobsite:backdrop-blur jobsite:transition-colors jobsite:hover:bg-black/75"
                      >
                        <ChevronLeft className="jobsite:h-5 jobsite:w-5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          moveSelection(1);
                        }}
                        aria-label={nextButtonLabel(selectedView, nextGroup)}
                        className="jobsite:absolute jobsite:right-0 jobsite:top-1/2 jobsite:flex jobsite:h-11 jobsite:w-11 jobsite:-translate-y-1/2 jobsite:items-center jobsite:justify-center jobsite:rounded-full jobsite:bg-black/55 jobsite:text-white jobsite:backdrop-blur jobsite:transition-colors jobsite:hover:bg-black/75"
                      >
                        <ChevronRight className="jobsite:h-5 jobsite:w-5" />
                      </button>
                    </>
                  )}
                </div>

                {selectedView.kind === "photo" &&
                (showsLightboxCaption || showsLightboxTag || groupStopCount > 1) && (
                  <div className="jobsite:flex jobsite:w-full jobsite:flex-wrap jobsite:items-center jobsite:justify-center jobsite:gap-x-3 jobsite:gap-y-2 jobsite:text-center">
                    {showsLightboxTag && (
                      <span
                        className="jobsite:bg-white/15 jobsite:px-3 jobsite:py-1 jobsite:text-[11px] jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.18em] jobsite:text-white"
                        style={surfaceRadiusStyle}
                      >
                        {selectedImage?.tag}
                      </span>
                    )}
                    {showsLightboxCaption && (
                      <span className="jobsite:font-heading jobsite:text-lg jobsite:text-white">
                        {selectedPhotoCaption}
                      </span>
                    )}
                    {groupStopCount > 1 && (
                      <span className="jobsite:text-sm jobsite:tabular-nums jobsite:text-white/70">
                        {selectedView.stopIndex + 1} / {groupStopCount}
                        {/* The project's name earns its place next to the count:
                            it is what tells a visitor which set they are inside. */}
                        {selectedGroup?.kind === "set" && (
                          <span className="jobsite:ml-2">{selectedGroup.label}</span>
                        )}
                      </span>
                    )}
                  </div>
                )}

                {selectedGroup !== null && groupStopCount > 1 && (
                  // The scroll container stays full width; the track inside is content-width and
                  // Auto-margined, so a few thumbs centre while many still scroll. Putting
                  // Justify-center on the scroll container itself would centre them but clip
                  // The first ones out of reach once they overflow.
                  <div
                    ref={thumbnailStripRef}
                    className="jobsite:w-full jobsite:min-w-0 jobsite:overflow-x-auto jobsite:px-1 jobsite:pb-1"
                  >
                    <div className="jobsite:mx-auto jobsite:flex jobsite:w-max jobsite:gap-2">
                    {selectedGroup.stops.map(({ photo }, stopIndex) => {
                      const isActive =
                        selectedView.kind === "photo" && stopIndex === selectedView.stopIndex;

                      return (
                        <button
                          key={`${photo.imageUrl}-${stopIndex}`}
                          type="button"
                          data-active={isActive}
                          aria-label={`Photo ${stopIndex + 1} of ${groupStopCount}`}
                          aria-current={isActive}
                          onClick={() => {
                            const view = viewIndexForStop(
                              views,
                              selectedView.groupIndex,
                              stopIndex,
                            );
                            if (view !== -1) {
                              setSelected(view);
                            }
                          }}
                          className={cn(
                            "jobsite:h-14 jobsite:w-20 jobsite:shrink-0 jobsite:overflow-hidden jobsite:rounded-md jobsite:transition-opacity",
                            isActive && "jobsite:opacity-100 jobsite:ring-2 jobsite:ring-white",
                            !isActive && "jobsite:opacity-50 jobsite:hover:opacity-80",
                          )}
                        >
                          <img
                            {...responsiveImageProps(photo.imageUrl, "thumbnail")}
                            alt=""
                            loading="lazy"
                            className="jobsite:h-full jobsite:w-full jobsite:object-cover"
                          />
                        </button>
                      );
                    })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  };


export type { GalleryPhoto, GalleryClickAction, GalleryImage, GalleryProps };
export { Gallery };
export type { GalleryPhotoStop, GalleryGroup, GalleryView } from "./gallery.groups";
